import {
  emitter,
  MESSAGE_EVENT_READY,
  MESSAGE_EVENT_RESET,
  MESSAGE_EVENT_CONTEXT,
  MESSAGE_EVENT_SAVED,
  MESSAGE_EVENT_HAS_UNSAVED,
  MESSAGE_EVENT_QUIZ_MOVED,
  MESSAGE_EVENT_BOOK_MOVED,
  MESSAGE_EVENT_TEST_INFO,
  MESSAGE_EVENT_INIT_REMOTE,
  MESSAGE_EVENT_CONTENT_CHANGED,
  MESSAGE_EVENT_CACHE_CLEAR,
  MESSAGE_EVENT_SOLVED,
  MESSAGE_EVENT_MOVE_CHAPTER,
  MESSAGE_EVENT_CLOSED,
  MESSAGE_EVENT_TEST_RESULT,
  MESSAGE_EVENT_RUN_COMMAND,
  MESSAGE_NOTIFY_ERROR,
  MESSAGE_EVENT_BOOK_COMPLETE,
  MESSAGE_EVENT_RUN_TESTS,
  MESSAGE_SAVE_ANSWERS,
  MESSAGE_PASTED,
  MessageHasUnsaved,
  MessageMoved,
  MESSAGE_QUIZ_PASTED,
  MESSAGE_ALERT_OVERLAY,
  MESSAGE_EVENT_RELOAD,
} from "./events";
import Logger from "./logger";
import Message from "./message";

/**
 * Interface
 */
interface ChallengeEditorMessageEvent {
  source?: string; // for handling postMessage which should ignore.
  name: string;
  data: {};
}

/**
 * Service class
 */
class ChallengeEditor {
  private context: Window;
  private ready = false;

  constructor(id: string) {
    const editor = document.getElementById(id) as HTMLIFrameElement;
    if (editor && editor.contentWindow) {
      this.context = editor.contentWindow;
      this.onMessage = this.onMessage.bind(this);
      window.addEventListener("message", this.onMessage);
    } else {
      Logger.warn(`editor context is not ready (id=${id})`);
    }
  }

  public sendEditorContext(
    context: {},
    language: string,
    currentPage?: number,
  ) {
    if (!this.context) {
      Logger.error("IFrame context is not mounted.");
    }
    if (!this.ready) {
      Logger.error("Editor is not ready.");
    }
    const newContext = Object.assign({}, context, { language });
    if (currentPage) {
      Object.assign(newContext, { currentPage });
    }

    this.postMessage({
      name: MESSAGE_EVENT_CONTEXT,
      data: newContext,
    });
  }

  public movePage(data: { page: number; showReview: boolean }) {
    this.postMessage({
      name: MESSAGE_EVENT_QUIZ_MOVED,
      data,
    } as ChallengeEditorMessageEvent);
  }

  public moveChapter(data: {}) {
    this.postMessage({
      name: MESSAGE_EVENT_MOVE_CHAPTER,
      data,
    } as ChallengeEditorMessageEvent);
  }

  public reset() {
    this.postMessage({
      name: MESSAGE_EVENT_RESET,
    } as ChallengeEditorMessageEvent);
  }

  public hadUnSaved() {
    this.postMessage({
      name: MESSAGE_EVENT_HAS_UNSAVED,
    } as ChallengeEditorMessageEvent);
  }

  public showTestInformation() {
    this.postMessage({
      name: MESSAGE_EVENT_TEST_INFO,
    } as ChallengeEditorMessageEvent);
  }

  public initializeRemote() {
    this.postMessage({
      name: MESSAGE_EVENT_INIT_REMOTE,
    } as ChallengeEditorMessageEvent);
  }

  public clearCache() {
    this.postMessage({
      name: MESSAGE_EVENT_CACHE_CLEAR,
    } as ChallengeEditorMessageEvent);
  }

  public runCommand(command: string) {
    this.postMessage({
      name: MESSAGE_EVENT_RUN_COMMAND,
      data: command,
    } as ChallengeEditorMessageEvent);
  }

  public save() {
    this.postMessage({
      name: MESSAGE_SAVE_ANSWERS,
    } as ChallengeEditorMessageEvent);
  }

  public showScreenshotPreventionAlert() {
    this.postMessage({
      name: MESSAGE_ALERT_OVERLAY,
      data: {
        message: Message.getMessageByKey("screenshotDetection.alert.message"),
        dismissButtonText: Message.getMessageByKey(
          "screenshotDetection.alert.dismissButton",
        ),
      },
    });
  }

  public destroy() {
    emitter.removeAllListeners();
    window.removeEventListener("message", this.onMessage);
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  public addListener(type: string, callback: Function) {
    emitter.addListener(type, callback);
  }

  public removeListener(type: string) {
    emitter.removeAllListeners(type);
  }

  private postMessage(data: ChallengeEditorMessageEvent) {
    if (!this.context) {
      Logger.error("IFrame context is not mounted.");
    }
    if (!this.ready) {
      Logger.error("Editor is not ready.");
    }

    Logger.info("postMessage", data);

    this.context.postMessage(data, "/");
  }

  private onMessage(e: MessageEvent) {
    const eventType = (e.data as ChallengeEditorMessageEvent).name;
    const source = (e.data as ChallengeEditorMessageEvent).source || "";

    const ignoreMessages = (
      process.env.REACT_APP_IGNORE_POST_MESSAGE_SOURCES || ""
    ).split(",");
    if (ignoreMessages.includes(source)) {
      return;
    }

    Logger.info("onMessage", e.data);

    switch (eventType) {
      case MESSAGE_EVENT_READY:
        this.ready = true;
        emitter.emit(MESSAGE_EVENT_READY);
        break;
      case MESSAGE_EVENT_RELOAD:
        this.ready = false;
        emitter.emit(MESSAGE_EVENT_RELOAD);
        break;
      case MESSAGE_EVENT_HAS_UNSAVED:
        emitter.emit(
          MESSAGE_EVENT_HAS_UNSAVED,
          (e.data as ChallengeEditorMessageEvent).data as MessageHasUnsaved,
        );
        break;
      case MESSAGE_EVENT_SAVED:
        emitter.emit(MESSAGE_EVENT_HAS_UNSAVED, {
          hasUnsaved: false,
        } as MessageHasUnsaved);
        emitter.emit(MESSAGE_EVENT_SAVED, e.data.data);
        break;
      case MESSAGE_EVENT_QUIZ_MOVED:
        const { page, showReview, historyReplace } = (
          e.data as ChallengeEditorMessageEvent
        ).data as MessageMoved;
        emitter.emit(MESSAGE_EVENT_QUIZ_MOVED, {
          page,
          showReview,
          historyReplace,
        });
        break;
      case MESSAGE_EVENT_BOOK_MOVED:
        emitter.emit(
          MESSAGE_EVENT_BOOK_MOVED,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_CONTENT_CHANGED:
        emitter.emit(MESSAGE_EVENT_HAS_UNSAVED, {
          hasUnsaved: true,
        } as MessageHasUnsaved);
        emitter.emit(MESSAGE_EVENT_CONTENT_CHANGED, e.data.data);
        break;
      case MESSAGE_EVENT_CACHE_CLEAR:
        emitter.emit(
          MESSAGE_EVENT_CACHE_CLEAR,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_SOLVED:
        emitter.emit(
          MESSAGE_EVENT_SOLVED,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_CLOSED:
        emitter.emit(
          MESSAGE_EVENT_CLOSED,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_TEST_RESULT:
        emitter.emit(
          MESSAGE_EVENT_TEST_RESULT,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_NOTIFY_ERROR:
        emitter.emit(
          MESSAGE_NOTIFY_ERROR,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_BOOK_COMPLETE:
        emitter.emit(
          MESSAGE_EVENT_BOOK_COMPLETE,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_EVENT_RUN_TESTS:
        emitter.emit(
          MESSAGE_EVENT_RUN_TESTS,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_PASTED:
        emitter.emit(
          MESSAGE_PASTED,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      case MESSAGE_QUIZ_PASTED:
        emitter.emit(
          MESSAGE_QUIZ_PASTED,
          (e.data as ChallengeEditorMessageEvent).data,
        );
        break;
      default:
        Logger.warn("Invalid event type: ", e.data);
    }
  }
}

export default ChallengeEditor;
